/**
 * NetworksAndGridsPanel - Panel unificado de Redes y Mallas
 *
 * @version 3.0
 * @created 2026-03-04
 * @updated 2026-03-05
 * @module core/layouts/panels/LeftPanel
 *
 * @description
 * Panel con 2 secciones:
 *
 * 1. RED URBANA:
 *    - Segmentos y Nodos (red canónica)
 *    - Malla Vial Base (vías IGN)
 *
 * 2. MALLAS H3:
 *    - Lista de mallas con controles de activación y visibilidad
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { AlertCircle, Power, Eye, EyeOff, Loader2, Hexagon, Network } from 'lucide-react';
import { useCensusRadioLayerStore } from '../../../census-2022/census-radio-layer-store';
import { useProject } from '../../../projects';
import {
  gridApi,
  useGrids,
  h3Service,
  getEffectiveStyle,
  ACTIVE_GRID_FILL_OPACITY,
  useUrbanNetwork,
  canonicalNetworkApi,
  baseNetworkApi,
  useBaseNetwork,
  useCanonicalNetworkLayer,
  useBaseNetworkLayer,
} from '../../../spatial';
import type { BaseNetworkStats } from '../../../spatial';
import { getActiveOpeningPerfSessionId, markOpeningPerfEvent } from '../../../utils';
import { useRightPanel } from '../RightPanel';
import { mapEngine } from '../../../map-engine/MapEngineService';
import type { SpatialGrid, H3GridFeatureCollection } from '../../../spatial';
import type { MapLayerMouseEvent } from 'maplibre-gl';
import { useMapTools } from '../../../map-tools';
import type { Tree } from '../../../map-tools/tree-types';
import './networks-and-grids-panel.css';

const SIDEBAR_INTEGER_FORMATTER = new Intl.NumberFormat('es-AR');

/**
 * Panel unificado de Redes y Mallas
 */
export function NetworksAndGridsPanel(): JSX.Element {
  const { projectId } = useProject();

  // ── UrbanNetworkContext (Segmentos y Nodos) ──
  const {
    isNetworkActive,
    setIsNetworkActive,
    isNetworkVisible,
    setIsNetworkVisible,
    networkStats,
    setNetworkStats,
    loadingStats,
    setLoadingStats,
  } = useUrbanNetwork();

  // ── BaseNetworkContext (Malla Vial Base) ──
  const {
    isBaseNetworkInteractive,
    setIsBaseNetworkInteractive,
    setIsRoadPanelOpen,
    roads,
    setRoads,
  } = useBaseNetwork();

  // ── GridsContext (Mallas H3) ──
  const {
    grids,
    setGrids,
    visibleGrids,
    setVisibleGrids,
    activeGridId,
    setActiveGridId,
    isGridInteractive,
    setIsGridInteractive,
    setCellRows,
    setHoveredCellId,
    setSelectedCellId,
    setCellPopoverPosition,
  } = useGrids();

  // ── RightPanel ──
  const { setActivePanel } = useRightPanel();

  // ── MapTools ──
  const { trees } = useMapTools();

  // ── Census radio layer store ──
  const censusVisible      = useCensusRadioLayerStore((s) => s.visible);
  const censusInteractive  = useCensusRadioLayerStore((s) => s.interactive);
  const toggleCensusVisible      = useCensusRadioLayerStore((s) => s.toggle);
  const toggleCensusInteractive  = useCensusRadioLayerStore((s) => s.toggleInteractive);

  // ── Local state ──
  const [error, setError] = useState<string | null>(null);
  const [loadingGrids, setLoadingGrids] = useState(true);
  const [loadingActive, setLoadingActive] = useState(false);
  const [loadingMap, setLoadingMap] = useState<Set<string>>(new Set());
  const [baseNetworkVisible, setBaseNetworkVisible] = useState(false); // Apagada por defecto
  const [baseNetworkStats, setBaseNetworkStats] = useState<BaseNetworkStats | null>(null);
  const [loadingBaseStats, setLoadingBaseStats] = useState(false);
  const [isActivatingNetwork, setIsActivatingNetwork] = useState(false);

  // ── Refs ──
  const geojsonCache = useRef<Map<string, H3GridFeatureCollection>>(new Map());
  const activeGridIdRef = useRef<string | null>(null);
  const hasAutoActivatedR9 = useRef(false);
  const hasAutoGeneratedNetwork = useRef(false);

  // ── Refs para interactividad H3 ──
  /** Celda actualmente bajo el cursor (para limpiar hovered state al salir) */
  const hoveredCellIdRef = useRef<string | null>(null);
  /** Celda actualmente seleccionada (para limpiar selected state al deseleccionar) */
  const selectedCellIdRef = useRef<string | null>(null);
  type H3MapHandlers = {
    gridId: string;
    mousemove: (e: MapLayerMouseEvent) => void;
    mouseleave: () => void;
    click: (e: MapLayerMouseEvent) => void;
  };
  /** Handlers activos registrados en el mapa (para poder hacer map.off correctamente) */
  const mapH3HandlerRef = useRef<H3MapHandlers | null>(null);

  useEffect(() => {
    activeGridIdRef.current = activeGridId;
  }, [activeGridId]);

  // ═══════════════════════════════════════════════════════════
  // RED URBANA - SEGMENTOS Y NODOS
  // ═══════════════════════════════════════════════════════════

  // Instrumentación mínima: marca el momento exacto en que el panel se monta
  // Permite distinguir si el retraso de network:stats-fetch-start ocurre
  // antes del mount del componente o durante/después.
  useEffect(() => {
    if (!projectId) return;
    const sessionId = getActiveOpeningPerfSessionId(projectId);
    markOpeningPerfEvent(sessionId, 'panel:networks-and-grids-mount');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadNetworkStats = useCallback(async () => {
    if (!projectId) return;
    const openingPerfSessionId = getActiveOpeningPerfSessionId(projectId);
    setLoadingStats(true);
    try {
      markOpeningPerfEvent(openingPerfSessionId, 'network:stats-fetch-start');
      const stats = await canonicalNetworkApi.getNetworkStats(projectId);
      markOpeningPerfEvent(openingPerfSessionId, 'network:stats-fetch-end');
      setNetworkStats(stats);
    } catch (err) {
      console.error('[NetworksAndGridsPanel] Error loading network stats:', err);
      setNetworkStats(null);
    } finally {
      setLoadingStats(false);
    }
  }, [projectId, setNetworkStats, setLoadingStats]);

  // ✨ CORRECCIÓN: Cargar stats automáticamente - Red Urbana es el estado base
  useEffect(() => {
    void loadNetworkStats();
  }, [loadNetworkStats]);

  const hasNetwork = networkStats && networkStats.edgeCount > 0;

  // Instrumentación: marca cuando hasNetwork pasa a true por primera vez
  // (i.e., stats respondió con edgeCount > 0 → hasData en useCanonicalNetworkLayer se activará)
  const hasNetworkRef = useRef(false);
  useEffect(() => {
    if (hasNetwork && !hasNetworkRef.current) {
      hasNetworkRef.current = true;
      const sessionId = getActiveOpeningPerfSessionId(projectId);
      markOpeningPerfEvent(sessionId, 'network:has-network-true');
    }
  }, [hasNetwork, projectId]);

  // Hook de capa de red canónica
  // ✨ CORRECCIÓN: Red Urbana siempre activa - isActive removido
  useCanonicalNetworkLayer({
    projectId,
    visible: isNetworkVisible,
    hasData: !!hasNetwork,
  });

  const handleToggleNetworkPower = useCallback(() => {
    setIsNetworkActive(!isNetworkActive);
    if (isNetworkActive) {
      setIsNetworkVisible(false);
    }
  }, [isNetworkActive, setIsNetworkActive, setIsNetworkVisible]);

  const handleToggleNetworkVisibility = useCallback(() => {
    setIsNetworkVisible(!isNetworkVisible);
  }, [isNetworkVisible, setIsNetworkVisible]);

  const handleNetworkNameClick = useCallback(() => {
    if (!hasNetwork) return;
    setActivePanel('network-style');
  }, [hasNetwork, setActivePanel]);

  // ═══════════════════════════════════════════════════════════
  // RED URBANA - MALLA VIAL BASE
  // ═══════════════════════════════════════════════════════════

  const loadBaseNetworkStats = useCallback(async () => {
    if (!projectId) return;
    setLoadingBaseStats(true);
    try {
      const stats = await baseNetworkApi.getStats(projectId);
      setBaseNetworkStats(stats);
    } catch (err) {
      console.error('[NetworksAndGridsPanel] Error loading base network stats:', err);
      setBaseNetworkStats(null);
    } finally {
      setLoadingBaseStats(false);
    }
  }, [projectId]);

  useEffect(() => {
    void loadBaseNetworkStats();
  }, [loadBaseNetworkStats]);

  const hasBaseNetwork = baseNetworkStats && baseNetworkStats.total > 0;

  // Debug logging para rastrear el estado de la red base
  useEffect(() => {
    console.log('[NetworksAndGridsPanel] Base network state:', {
      hasBaseNetwork: !!hasBaseNetwork,
      baseNetworkVisible,
      baseNetworkStats,
      projectId,
    });
  }, [hasBaseNetwork, baseNetworkVisible, baseNetworkStats, projectId]);

  // Hook de capa de vías base
  useBaseNetworkLayer({
    projectId,
    visible: baseNetworkVisible,
    hasData: !!hasBaseNetwork,
  });

  const handleToggleBaseNetworkVisibility = useCallback(() => {
    const newValue = !baseNetworkVisible;
    console.log('[NetworksAndGridsPanel] Toggle base network visibility:', {
      from: baseNetworkVisible,
      to: newValue,
      hasBaseNetwork: !!hasBaseNetwork,
      baseNetworkStats,
      projectId,
    });
    setBaseNetworkVisible(newValue);
  }, [baseNetworkVisible, hasBaseNetwork, baseNetworkStats, projectId]);

  const loadRoads = useCallback(async () => {
    if (!projectId) return;
    try {
      const geojson = await baseNetworkApi.getGeoJSON(projectId);
      const roadsData = geojson.features.map(feature => ({
        id: feature.id as string,
        category: feature.properties.category,
        rtn: feature.properties.rtn || null,
        rst: feature.properties.rst || null,
        typ: feature.properties.typ || null,
        hct: feature.properties.hct || null,
        fdc: feature.properties.fdc || null,
        gid: feature.properties.gid || null,
      }));
      setRoads(roadsData);
    } catch (err) {
      console.error('[NetworksAndGridsPanel] Error loading roads:', err);
    }
  }, [projectId, setRoads]);

  const handleBaseNetworkNameClick = useCallback(async () => {
    if (!hasBaseNetwork) return;
    if (roads.length === 0) {
      await loadRoads();
    }
    setActivePanel('base-roads');
    setIsRoadPanelOpen(true);
    if (!isBaseNetworkInteractive) {
      setIsBaseNetworkInteractive(true);
    }
  }, [hasBaseNetwork, roads.length, loadRoads, setActivePanel, setIsRoadPanelOpen, isBaseNetworkInteractive, setIsBaseNetworkInteractive]);

  // ═══════════════════════════════════════════════════════════
  // AUTO-GENERACIÓN DE RED URBANA
  // ═══════════════════════════════════════════════════════════

  /**
   * Activa la red urbana convirtiendo vías base en nodos + edges
   */
  const handleActivateNetwork = useCallback(async () => {
    if (!projectId) return;

    setIsActivatingNetwork(true);
    setError(null);

    try {
      console.log('[NetworksAndGridsPanel] 🚀 Iniciando conversión de vías base a red urbana...');
      const result = await canonicalNetworkApi.activateNetwork(projectId);

      console.log('[NetworksAndGridsPanel] ✅ Red urbana generada:', result);
      console.log(`[NetworksAndGridsPanel] 📊 ${result.edgesCreated.toLocaleString()} segmentos creados en ${(result.duration / 1000).toFixed(1)}s`);

      // Recargar estadísticas
      console.log('[NetworksAndGridsPanel] 🔄 Recargando estadísticas...');
      await loadNetworkStats();

      console.log('[NetworksAndGridsPanel] 📊 Estadísticas recargadas');

      // UX: Solo mostrar la red (edges), NO activar Power (nodos ocultos)
      console.log('[NetworksAndGridsPanel] 👁️ Mostrando red (solo líneas)...');
      setIsNetworkVisible(true);
      // NO activar isNetworkActive - el usuario debe hacerlo manualmente para ver nodos

      console.log('[NetworksAndGridsPanel] ✅ Red visible (nodos ocultos hasta activar Power)');
    } catch (err: any) {
      console.error('[NetworksAndGridsPanel] ❌ Error generando red:', err);
      const errorMsg = err.message || 'Error al generar red urbana';
      setError(errorMsg);
      console.error('[NetworksAndGridsPanel] Error:', errorMsg);
    } finally {
      setIsActivatingNetwork(false);
    }
  }, [projectId, loadNetworkStats, setIsNetworkActive, setIsNetworkVisible]);

  // Reset auto-generation flag cuando cambia el proyecto
  useEffect(() => {
    hasAutoGeneratedNetwork.current = false;
  }, [projectId]);

  // Auto-generar red urbana cuando hay vías base pero no red canónica
  useEffect(() => {
    const shouldAutoGenerate =
      projectId &&
      hasBaseNetwork &&
      !hasNetwork &&
      !isActivatingNetwork &&
      !loadingStats &&
      !loadingBaseStats &&
      !hasAutoGeneratedNetwork.current;

    if (shouldAutoGenerate) {
      console.log('[NetworksAndGridsPanel] 🤖 Auto-generando red urbana desde vías base...');
      hasAutoGeneratedNetwork.current = true;
      void handleActivateNetwork();
    }
  }, [projectId, hasBaseNetwork, hasNetwork, isActivatingNetwork, loadingStats, loadingBaseStats, handleActivateNetwork]);

  // ═══════════════════════════════════════════════════════════
  // MALLAS H3
  // ═══════════════════════════════════════════════════════════

  /**
   * Carga las mallas del proyecto desde la API.
   * NO incluye auto-activación de R9 para evitar complejidad y posibles loops.
   * La auto-activación se maneja en un useEffect separado.
   */
  const loadGrids = useCallback(async () => {
    if (!projectId) {
      setLoadingGrids(false);
      return;
    }
    setLoadingGrids(true);
    setError(null);
    try {
      const loadedGrids = await gridApi.list(projectId);
      setGrids(loadedGrids);
      console.log('[NetworksAndGridsPanel] Grids loaded:', loadedGrids.length);
    } catch {
      setError('Error al cargar las mallas');
    } finally {
      setLoadingGrids(false);
    }
  }, [projectId, setGrids]);

  useEffect(() => {
    void loadGrids();
  }, [loadGrids]);

  // Reset auto-activation flag cuando cambia el proyecto
  useEffect(() => {
    hasAutoActivatedR9.current = false;
  }, [projectId]);

  /**
   * Auto-activación de R9 - DESACTIVADO para mejorar UX
   * El usuario ahora debe activar manualmente la malla H3 que desee ver
   *
   * HISTORIAL:
   * - Antes se auto-activaba R9 al cargar el proyecto
   * - Se desactivó para no saturar visualmente al usuario
   * - La malla se activa manualmente desde el panel izquierdo
   */
  useEffect(() => {
    // UX: Desactivado para evitar saturación visual al inicio
    // El usuario activa manualmente la malla que desea ver
    return;

    // Código original comentado:
    /*
    // No ejecutar si estamos cargando o ya auto-activamos
    if (loadingGrids || hasAutoActivatedR9.current || grids.length === 0) {
      return;
    }

    const r9Grid = grids.find(g => g.resolution === 9);
    if (!r9Grid) {
      console.log('[NetworksAndGridsPanel] No R9 grid found for auto-activation');
      return;
    }

    // Marcar ANTES de hacer nada async
    hasAutoActivatedR9.current = true;
    console.log('[NetworksAndGridsPanel] Auto-activating R9 grid:', r9Grid.id);

    // Función para intentar visualizar la malla
    const tryVisualize = async (): Promise<void> => {
      try {
        const geojson = await getGeoJSON(r9Grid.id);

        // Esperar a que el estilo del mapa esté cargado (no todos los tiles, solo el estilo)
        const waitForMapStyle = (): Promise<ReturnType<typeof mapEngine.getMap> | null> => {
          return new Promise((resolve) => {
            let attempts = 0;
            const maxAttempts = 50; // 5 segundos

            const check = (): void => {
              const map = mapEngine.getMap();
              if (map && map.isStyleLoaded()) {
                resolve(map);
              } else if (++attempts >= maxAttempts) {
                console.warn('[NetworksAndGridsPanel] Map style not ready after 5s, skipping R9 auto-activation');
                resolve(null);
              } else {
                setTimeout(check, 100);
              }
            };

            check();
          });
        };

        const map = await waitForMapStyle();
        if (map) {
          console.log('[NetworksAndGridsPanel] Map style ready, visualizing R9');
          visualizeGrid(r9Grid, geojson);
          setVisibleGrids(new Set([r9Grid.id]));
        }
      } catch (err) {
        console.error('[NetworksAndGridsPanel] Error auto-activating R9:', err);
      }
    };

    void tryVisualize();
    // Dependencias mínimas: solo reaccionar a cambios en grids y loadingGrids
    // setVisibleGrids es estable (viene de useState)
    */
  }, [loadingGrids, grids, setVisibleGrids]);

  useEffect(() => {
    const handler = (): void => {
      void loadGrids();
    };
    window.addEventListener('grid-created', handler);
    return () => window.removeEventListener('grid-created', handler);
  }, [loadGrids]);

  // Actualizar cell rows cuando cambian árboles
  useEffect(() => {
    if (!activeGridId) return;
    const activeGrid = grids.find(g => g.id === activeGridId);
    if (!activeGrid) return;
    setCellRows(buildCellRows(activeGrid, trees));
  }, [activeGridId, grids, trees, setCellRows]);

  /**
   * Interactividad H3: registra/desregistra handlers y actualiza fill-opacity.
   *
   * Cuando isGridInteractive === true:
   *   - Registra map.on('click/mousemove/mouseleave', fillLayerId, handler)
   *   - Aplica expresión de feature-state para opacity (base 0.15, hover 0.35, selected 0.5)
   *
   * Cuando isGridInteractive === false (o no hay activeGridId):
   *   - Elimina handlers
   *   - Restaura opacity plana a 0.02
   *   - Limpia feature states
   *
   * Capas no activas: siempre a 0.02 (sin interactividad)
   */
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !map.isStyleLoaded()) return;

    // ── Mallas visibles no activas: opacity plana 0.02 ──
    visibleGrids.forEach(gridId => {
      if (gridId === activeGridId) return; // La activa se maneja abajo
      const fId = `h3-grid-fill-${gridId}`;
      if (map.getLayer(fId)) map.setPaintProperty(fId, 'fill-opacity', 0.02);
    });

    // ── Sin malla activa o interactividad desactivada → limpiar ──
    if (!activeGridId || !isGridInteractive) {
      if (mapH3HandlerRef.current) {
        const { gridId: hId, mousemove, mouseleave, click } = mapH3HandlerRef.current;
        const fId = `h3-grid-fill-${hId}`;
        map.off('mousemove', fId, mousemove);
        map.off('mouseleave', fId, mouseleave);
        map.off('click',     fId, click);
        if (map.getLayer(fId)) {
          map.setPaintProperty(fId, 'fill-opacity', 0.02);
        }
        const sId = `h3-grid-${hId}`;
        if (map.getSource(sId)) map.removeFeatureState({ source: sId });
        mapH3HandlerRef.current = null;
      }
      if (activeGridId) {
        const fId = `h3-grid-fill-${activeGridId}`;
        if (map.getLayer(fId)) map.setPaintProperty(fId, 'fill-opacity', 0.02);
      }
      map.getCanvas().style.cursor = '';
      hoveredCellIdRef.current = null;
      selectedCellIdRef.current = null;
      return;
    }

    // ── Malla activa E interactiva ──
    const fillLayerId = `h3-grid-fill-${activeGridId}`;
    const sourceId    = `h3-grid-${activeGridId}`;

    if (!map.getLayer(fillLayerId)) {
      const h3Layers = map.getStyle()?.layers
        ?.filter(l => l.id.startsWith('h3-grid'))
        .map(l => l.id) ?? [];
      console.warn(
        '[NetworksAndGridsPanel] ⚠ H3 fill layer no encontrado al activar interactividad:', fillLayerId,
        '\n  Capas h3 presentes:', h3Layers.length ? h3Layers : '(ninguna)',
        '\n  Source existe:', !!map.getSource(sourceId),
      );
      return;
    }

    // Limpiar handlers previos para esta misma malla (efecto re-ejecutado)
    if (mapH3HandlerRef.current?.gridId === activeGridId) {
      const { mousemove: pm, mouseleave: pl, click: pc } = mapH3HandlerRef.current;
      map.off('mousemove', fillLayerId, pm);
      map.off('mouseleave', fillLayerId, pl);
      map.off('click',     fillLayerId, pc);
      mapH3HandlerRef.current = null;
      console.log('[NetworksAndGridsPanel] Handlers previos limpiados para re-registro:', fillLayerId);
    }

    // Estilo efectivo para las expresiones de opacidad
    const activeGrid = grids.find(g => g.id === activeGridId);
    const s = activeGrid ? getEffectiveStyle(activeGrid) : null;
    const hoverOpacity    = s?.hoverOpacity    ?? 0.35;
    const selectedOpacity = s?.selectedOpacity ?? 0.5;

    // Aplicar expresión de feature-state: base 0.15, hover 0.35, selected 0.5
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.setPaintProperty(fillLayerId, 'fill-opacity', ['case',
      ['boolean', ['feature-state', 'selected'], false], selectedOpacity,
      ['boolean', ['feature-state', 'hovered'],  false], hoverOpacity,
      ACTIVE_GRID_FILL_OPACITY,
    ] as any);

    // ── Handlers ──
    const mousemove = (e: MapLayerMouseEvent): void => {
      const h3Index = e.features?.[0]?.properties?.h3Index as string | undefined;
      if (!h3Index || !map.getSource(sourceId)) return;
      map.getCanvas().style.cursor = 'pointer';
      if (h3Index !== hoveredCellIdRef.current) {
        if (hoveredCellIdRef.current) {
          map.setFeatureState({ source: sourceId, id: hoveredCellIdRef.current }, { hovered: false });
        }
        map.setFeatureState({ source: sourceId, id: h3Index }, { hovered: true });
        hoveredCellIdRef.current = h3Index;
        setHoveredCellId(h3Index);
      }
    };

    const mouseleave = (): void => {
      if (hoveredCellIdRef.current && map.getSource(sourceId)) {
        map.setFeatureState({ source: sourceId, id: hoveredCellIdRef.current }, { hovered: false });
      }
      map.getCanvas().style.cursor = '';
      hoveredCellIdRef.current = null;
      setHoveredCellId(null);
    };

    const click = (e: MapLayerMouseEvent): void => {
      const h3Index = e.features?.[0]?.properties?.h3Index as string | undefined;
      if (!h3Index || !map.getSource(sourceId)) {
        return;
      }
      // Toggle: clic en la misma celda la deselecciona
      const next = selectedCellIdRef.current === h3Index ? null : h3Index;
      if (selectedCellIdRef.current) {
        map.setFeatureState({ source: sourceId, id: selectedCellIdRef.current }, { selected: false });
      }
      if (next) {
        map.setFeatureState({ source: sourceId, id: next }, { selected: true });
        const { x, y } = e.point;
        setCellPopoverPosition({ x, y });
      } else {
        setCellPopoverPosition(null);
      }
      selectedCellIdRef.current = next;
      setSelectedCellId(next);
    };

    map.on('mousemove', fillLayerId, mousemove);
    map.on('mouseleave', fillLayerId, mouseleave);
    map.on('click',     fillLayerId, click);
    mapH3HandlerRef.current = { gridId: activeGridId, mousemove, mouseleave, click };

    // Cleanup: se ejecuta cuando el efecto re-corre o el componente se desmonta
    return () => {
      map.off('mousemove', fillLayerId, mousemove);
      map.off('mouseleave', fillLayerId, mouseleave);
      map.off('click',     fillLayerId, click);
      if (mapH3HandlerRef.current?.gridId === activeGridId) {
        mapH3HandlerRef.current = null;
      }
      if (map.getLayer(fillLayerId)) {
        map.setPaintProperty(fillLayerId, 'fill-opacity', 0.02);
      }
      const srcId = `h3-grid-${activeGridId}`;
      if (map.getSource(srcId)) map.removeFeatureState({ source: srcId });
      map.getCanvas().style.cursor = '';
      hoveredCellIdRef.current = null;
      selectedCellIdRef.current = null;
    };
  // grids excluido intencionalmente de deps: el estilo se resuelve al activar interactividad
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGridInteractive, activeGridId, visibleGrids]);

  /**
   * Construye filas de celdas con conteos de elementos
   */
  function buildCellRows(grid: SpatialGrid, trees: Tree[]) {
    const cellCounts = new Map<string, Record<string, number>>();
    grid.cells.forEach(h3Index => {
      cellCounts.set(h3Index, {
        trees: 0,
        parks: 0,
        sunshades: 0,
        fountains: 0,
        waterCourses: 0,
        waterMirrors: 0,
      });
    });
    trees.forEach(tree => {
      const h3Index = h3Service.latLngToH3(tree.coordinates[1], tree.coordinates[0], grid.resolution);
      const counts = cellCounts.get(h3Index);
      if (counts) {
        counts.trees += 1;
      }
    });
    return grid.cells.map(h3Index => ({
      h3Index,
      cellCode: grid.cellCodes?.[h3Index] ?? null,
      cellName: grid.cellNames?.[h3Index] ?? null,
      env: cellCounts.get(h3Index) ?? {},
    }));
  }

  function formatCount(value: number): string {
    return SIDEBAR_INTEGER_FORMATTER.format(value);
  }

  /**
   * Obtiene GeoJSON de malla (con cache)
   */
  async function getGeoJSON(gridId: string): Promise<H3GridFeatureCollection> {
    const cached = geojsonCache.current.get(gridId);
    if (cached) return cached;
    const geojson = await gridApi.getGeoJSON(gridId);
    geojsonCache.current.set(gridId, geojson);
    return geojson;
  }

  /**
   * Visualiza malla en el mapa
   *
   * NOTA: Usa isStyleLoaded() en vez de loaded() porque:
   * - loaded() retorna true solo cuando TODOS los tiles están cargados
   * - isStyleLoaded() retorna true cuando el estilo está listo para agregar capas
   */
  function visualizeGrid(grid: SpatialGrid, geojson: H3GridFeatureCollection): void {
    console.log('[NetworksAndGridsPanel] visualizeGrid called for:', grid.id, grid.name);

    const map = mapEngine.getMap();

    if (!map) {
      console.error('[NetworksAndGridsPanel] Map is null, cannot visualize grid');
      return;
    }

    // Usar isStyleLoaded() en vez de loaded() - más confiable para agregar capas
    if (!map.isStyleLoaded()) {
      console.warn('[NetworksAndGridsPanel] Map style not loaded yet, waiting for style...');
      // Esperar a que el estilo se cargue y reintentar
      map.once('styledata', () => {
        console.log('[NetworksAndGridsPanel] Style loaded, retrying visualization');
        visualizeGrid(grid, geojson);
      });
      return;
    }

    console.log('[NetworksAndGridsPanel] Map style is loaded, proceeding with visualization');

    const sourceId = `h3-grid-${grid.id}`;
    const fillLayerId = `h3-grid-fill-${grid.id}`;
    const outlineLayerId = `h3-grid-outline-${grid.id}`;

    // Check if layers already exist
    const fillLayerExists = map.getLayer(fillLayerId);
    const outlineLayerExists = map.getLayer(outlineLayerId);

    console.log('[NetworksAndGridsPanel] Layer existence check:', {
      fillLayerExists: !!fillLayerExists,
      outlineLayerExists: !!outlineLayerExists
    });

    // Si las capas ya existen, no hacer nada (evitar loop)
    if (fillLayerExists && outlineLayerExists) {
      console.log('[NetworksAndGridsPanel] Both layers already exist, skipping visualization');
      return;
    }

    // Remover capas existentes solo si es necesario
    if (fillLayerExists) {
      console.log('[NetworksAndGridsPanel] Removing existing fill layer');
      map.removeLayer(fillLayerId);
    }
    if (outlineLayerExists) {
      console.log('[NetworksAndGridsPanel] Removing existing outline layer');
      map.removeLayer(outlineLayerId);
    }
    if (map.getSource(sourceId)) {
      console.log('[NetworksAndGridsPanel] Removing existing source');
      map.removeSource(sourceId);
    }

    // Crear source
    // promoteId: usa h3Index como ID de feature → habilita setFeatureState O(1)
    console.log('[NetworksAndGridsPanel] Adding source with', geojson.features.length, 'features');
    map.addSource(sourceId, {
      type: 'geojson',
      data: geojson,
      promoteId: 'h3Index',
    });

    const style = getEffectiveStyle(grid);
    console.log('[NetworksAndGridsPanel] Using style:', style);

    // Layer de relleno - siempre inicia casi transparente (0.02)
    // La opacidad aumenta a 0.15 solo cuando la malla está activa E interactiva
    console.log('[NetworksAndGridsPanel] Adding fill layer:', fillLayerId);
    map.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': style.fillColor,
        'fill-opacity': 0.02, // Siempre casi transparente al crear
      },
    });

    // Layer de contorno (borde de hexágonos)
    console.log('[NetworksAndGridsPanel] Adding outline layer:', outlineLayerId);
    map.addLayer({
      id: outlineLayerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': style.lineColor,
        'line-width': style.lineWidth,
        'line-opacity': 1.0, // Borde siempre visible al 100%
      },
    });

    console.log('[NetworksAndGridsPanel] Grid visualization complete for:', grid.id);
  }

  /**
   * Oculta malla en el mapa
   */
  function hideGrid(gridId: string): void {
    console.log('[NetworksAndGridsPanel] hideGrid called for:', gridId);
    const map = mapEngine.getMap();

    if (!map) {
      console.error('[NetworksAndGridsPanel] Map is null, cannot hide grid');
      return;
    }

    const fillLayerId = `h3-grid-fill-${gridId}`;
    const outlineLayerId = `h3-grid-outline-${gridId}`;
    const sourceId = `h3-grid-${gridId}`;

    if (map.getLayer(fillLayerId)) {
      console.log('[NetworksAndGridsPanel] Removing fill layer');
      map.removeLayer(fillLayerId);
    }
    if (map.getLayer(outlineLayerId)) {
      console.log('[NetworksAndGridsPanel] Removing outline layer');
      map.removeLayer(outlineLayerId);
    }
    if (map.getSource(sourceId)) {
      console.log('[NetworksAndGridsPanel] Removing source');
      map.removeSource(sourceId);
    }

    console.log('[NetworksAndGridsPanel] Grid hidden:', gridId);
  }

  /**
   * Toggle activo de malla (Power)
   */
  const handleToggleActive = async (grid: SpatialGrid): Promise<void> => {
    const isCurrentlyActive = activeGridId === grid.id;

    if (isCurrentlyActive) {
      // Desactivar
      setActiveGridId(null);
      setIsGridInteractive(false);
      setCellRows([]);
      setActivePanel(null);
    } else {
      // Activar
      setLoadingActive(true);
      try {
        // Asegurar que esté visible
        if (!visibleGrids.has(grid.id)) {
          const geojson = await getGeoJSON(grid.id);
          visualizeGrid(grid, geojson);
          setVisibleGrids(prev => new Set(prev).add(grid.id));
        }
        setActiveGridId(grid.id);
        setIsGridInteractive(false);
        setCellRows(buildCellRows(grid, trees));
      } catch (err) {
        console.error('[NetworksAndGridsPanel] Error activating grid:', err);
      } finally {
        setLoadingActive(false);
      }
    }
  };

  /**
   * Toggle visibilidad en mapa (Eye)
   */
  const handleToggleMap = async (grid: SpatialGrid): Promise<void> => {
    console.log('[NetworksAndGridsPanel] handleToggleMap called for:', grid.id, grid.name);
    const isCurrentlyVisible = visibleGrids.has(grid.id);
    console.log('[NetworksAndGridsPanel] Currently visible:', isCurrentlyVisible);

    if (isCurrentlyVisible) {
      // Ocultar
      console.log('[NetworksAndGridsPanel] Hiding grid:', grid.id);
      hideGrid(grid.id);
      setVisibleGrids(prev => {
        const s = new Set(prev);
        s.delete(grid.id);
        console.log('[NetworksAndGridsPanel] Updated visibleGrids (removed):', Array.from(s));
        return s;
      });
    } else {
      // Mostrar
      console.log('[NetworksAndGridsPanel] Showing grid:', grid.id);
      setLoadingMap(prev => new Set(prev).add(grid.id));
      try {
        console.log('[NetworksAndGridsPanel] Fetching GeoJSON for:', grid.id);
        const geojson = await getGeoJSON(grid.id);
        console.log('[NetworksAndGridsPanel] GeoJSON fetched, features:', geojson.features.length);

        visualizeGrid(grid, geojson);

        setVisibleGrids(prev => {
          const s = new Set(prev).add(grid.id);
          console.log('[NetworksAndGridsPanel] Updated visibleGrids (added):', Array.from(s));
          return s;
        });
      } catch (err) {
        console.error('[NetworksAndGridsPanel] Error toggling map:', err);
      } finally {
        setLoadingMap(prev => {
          const s = new Set(prev);
          s.delete(grid.id);
          return s;
        });
      }
    }
  };

  /**
   * Click en nombre de malla abre editor de estilos
   */
  const handleGridNameClick = (grid: SpatialGrid): void => {
    window.dispatchEvent(new CustomEvent('grid-edit-start', { detail: grid }));
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  if (!projectId) {
    return (
      <div className="nagp-state">
        <AlertCircle size={20} />
        <p>No hay proyecto activo</p>
      </div>
    );
  }

  return (
    <div className="networks-and-grids-panel">
      {/* ═══════════════════════════════════════════════════════════
          CARD 1: CAPAS (capas base / estructurales)
          ═══════════════════════════════════════════════════════════ */}
      <section className="nagp-section nagp-section--layers" aria-label="Capas">
        {/* Header */}
        <div className="nagp-grids-header">
          <Network size={12} className="nagp-grids-icon" />
          <span className="nagp-grids-label">Capas</span>
        </div>

        {/* Fila: Segmentos y Nodos */}
        <div className={`nagp-grid-row${isNetworkActive ? ' active' : ''}`}>
          {/* Power */}
          <button
            className={`nagp-toggle${isNetworkActive ? ' on' : ''}`}
            onClick={handleToggleNetworkPower}
            aria-pressed={isNetworkActive}
            title={isNetworkActive ? 'Deshabilitar red' : 'Habilitar red'}
          >
            <Power size={10} />
          </button>

          {/* Eye */}
          <button
            className={`nagp-eye${isNetworkVisible ? ' on' : ''}`}
            onClick={handleToggleNetworkVisibility}
            disabled={!hasNetwork}
            aria-pressed={isNetworkVisible}
            title={!hasNetwork ? 'Sin red' : isNetworkVisible ? 'Ocultar red' : 'Mostrar red'}
          >
            {isNetworkVisible ? <Eye size={9} /> : <EyeOff size={9} />}
          </button>

          {/* Nombre */}
          <span
            className={`nagp-grid-name nagp-grid-name--layers${hasNetwork ? ' clickable' : ''}`}
            onClick={handleNetworkNameClick}
            title={hasNetwork ? `Red urbana · ${formatCount(networkStats.edgeCount)} segmentos` : 'Red urbana'}
          >
            Red urbana
          </span>

          {/* Contador */}
          {loadingStats ? (
            <Loader2 size={10} className="nagp-spinner" />
          ) : hasNetwork ? (
            <span className="nagp-grid-count">{formatCount(networkStats.edgeCount)}</span>
          ) : (
            <span className="nagp-grid-count nagp-count--empty">—</span>
          )}
        </div>

        {/* Fila: Malla Vial Base */}
        <div className={`nagp-grid-row${baseNetworkVisible ? ' active' : ''}`}>
          {/* Spacer para power (no tiene) */}
          <div className="nagp-toggle-spacer" />

          {/* Eye */}
          <button
            className={`nagp-eye${baseNetworkVisible ? ' on' : ''}`}
            onClick={handleToggleBaseNetworkVisibility}
            disabled={!hasBaseNetwork}
            aria-pressed={baseNetworkVisible}
            title={!hasBaseNetwork ? 'Sin datos' : baseNetworkVisible ? 'Ocultar vías' : 'Mostrar vías'}
          >
            {baseNetworkVisible ? <Eye size={9} /> : <EyeOff size={9} />}
          </button>

          {/* Nombre */}
          <span
            className={`nagp-grid-name nagp-grid-name--layers${hasBaseNetwork ? ' clickable' : ''}`}
            onClick={handleBaseNetworkNameClick}
            title={hasBaseNetwork ? `Malla base · ${formatCount(baseNetworkStats.total)} celdas` : 'Malla base'}
          >
            Malla base
          </span>

          {/* Contador */}
          {loadingBaseStats ? (
            <Loader2 size={10} className="nagp-spinner" />
          ) : hasBaseNetwork ? (
            <span className="nagp-grid-count">{formatCount(baseNetworkStats.total)}</span>
          ) : (
            <span className="nagp-grid-count nagp-count--empty">—</span>
          )}
        </div>

        {/* Fila: Radios Censales 2022 */}
        <div className={`nagp-grid-row${censusInteractive ? ' active' : ''}${censusVisible && !censusInteractive ? ' visible-only' : ''}`}>
          {/* Power → interactive */}
          <button
            className={`nagp-toggle${censusInteractive ? ' on' : ''}`}
            onClick={censusVisible ? toggleCensusInteractive : undefined}
            disabled={!censusVisible}
            aria-pressed={censusInteractive}
            title={!censusVisible ? 'Activa la capa primero' : censusInteractive ? 'Desactivar clic en radios' : 'Activar clic en radios'}
          >
            <Power size={10} />
          </button>

          {/* Eye → visible */}
          <button
            className={`nagp-eye${censusVisible ? ' on' : ''}`}
            onClick={toggleCensusVisible}
            aria-pressed={censusVisible}
            title={censusVisible ? 'Ocultar radios censales' : 'Mostrar radios censales'}
          >
            {censusVisible ? <Eye size={9} /> : <EyeOff size={9} />}
          </button>

          {/* Nombre */}
          <span className="nagp-grid-name nagp-grid-name--layers nagp-grid-name--census">
            Censo 2022
          </span>

          {/* Sin contador — datos se ven en tab Censo */}
          <span className="nagp-grid-count nagp-count--empty" />
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          CARD 2: MALLAS H3 (mallas analíticas)
          ═══════════════════════════════════════════════════════════ */}
      <section className="nagp-section nagp-section--grids" aria-label="Mallas H3">
        {/* Header */}
        <div className="nagp-grids-header">
          <Hexagon size={12} className="nagp-grids-icon" />
          <span className="nagp-grids-label">Mallas H3</span>
        </div>

        {/* Error */}
        {error && (
          <div className="nagp-error">
            <AlertCircle size={12} />
            <span>{error}</span>
          </div>
        )}

        {/* Loading — fila inline compacta */}
        {loadingGrids && (
          <div className="nagp-state">
            <Loader2 size={11} className="nagp-spinner" />
            <p>Cargando…</p>
          </div>
        )}

        {/* Empty */}
        {!loadingGrids && !error && grids.length === 0 && (
          <div className="nagp-state nagp-state--empty">
            <p>Sin mallas</p>
          </div>
        )}

        {/* Lista de mallas */}
        {!loadingGrids &&
          !error &&
          grids.map(grid => {
            const isActive = activeGridId === grid.id;
            const isVisible = visibleGrids.has(grid.id);
            const isLoadingMap = loadingMap.has(grid.id);
            const isPrimary = grid.resolution === 9;

            return (
              <div
                key={grid.id}
                className={`nagp-grid-row${isActive ? ' active' : ''}${isPrimary ? ' primary' : ''}`}
              >
                {/* Power */}
                <button
                  className={`nagp-toggle${isActive ? ' on' : ''}`}
                  onClick={() => void handleToggleActive(grid)}
                  disabled={loadingActive && !isActive}
                  aria-pressed={isActive}
                  title={isActive ? 'Desactivar malla' : 'Activar malla'}
                >
                  {loadingActive && !isActive ? (
                    <Loader2 size={10} className="nagp-spinner" />
                  ) : (
                    <Power size={10} />
                  )}
                </button>

                {/* Eye */}
                <button
                  className={`nagp-eye${isVisible ? ' on' : ''}`}
                  onClick={() => void handleToggleMap(grid)}
                  disabled={isLoadingMap}
                  aria-pressed={isVisible}
                  title={isVisible ? 'Ocultar en mapa' : 'Mostrar en mapa'}
                >
                  {isLoadingMap ? (
                    <Loader2 size={9} className="nagp-spinner" />
                  ) : isVisible ? (
                    <Eye size={9} />
                  ) : (
                    <EyeOff size={9} />
                  )}
                </button>

                {/* Resolución */}
                <span
                  className="nagp-grid-resolution"
                  onClick={() => handleGridNameClick(grid)}
                  title={grid.name}
                >
                  R{grid.resolution}
                </span>

                <span className="nagp-grid-spacer" aria-hidden="true" />

                {/* Contador de celdas */}
                <span className="nagp-grid-count">{formatCount(grid.metadata.cellCount)}</span>

                {/* Color */}
                <div
                  className="nagp-grid-color"
                  style={{ backgroundColor: getEffectiveStyle(grid).fillColor }}
                />
              </div>
            );
          })}
      </section>
    </div>
  );
}
